// SPDX-License-Identifier: Elastic-2.0
/**
 * Router config (T-004).
 *
 * React Router 6 data-router API (also exported by react-router-dom v7,
 * which is what this repo currently installs). Mounts the v0.1 route
 * map and wires the two guards.
 *
 * Route map (per task T-004 + functional spec § 2):
 *   /                       → index, redirects per guards
 *   /onboarding             → OnboardingPage (no guards)
 *   /onboarding/account     → SignInPage (re-auth entry, no guards)
 *   /dashboard              ┐
 *   /routines               │ behind OnboardingGuard + AuthGuard
 *   /suggestions            │
 *   /settings               ┘
 *   /offline                → OfflinePage (no guards)
 *   *                       → NotFoundPage
 *
 * (The former `/ha` Advanced-mode link was removed in F-357 / ADR-010 — no
 * Home Assistant UI surface ships.)
 *
 * Helpers / placeholder components live in sibling files so this
 * module stays pure-config-plus-tree (Fast Refresh requires it).
 */
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import { AuthGuard } from "./AuthGuard";
import { OnboardingGuard } from "./OnboardingGuard";
import { ROUTES } from "./routes";
import { rootIndexLoader } from "./rootLoader";
import { SuggestionsListPage } from "../features/suggestions/SuggestionsListPage";

import { DashboardLayout } from "../features/dashboard/DashboardLayout";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { OnboardingPage } from "../features/onboarding/OnboardingPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { SignInPage } from "../features/auth/SignInPage";
import { NotFoundPage } from "../features/NotFoundPage";
import { OfflinePage } from "../features/OfflinePage";
import { RoutinesListPage } from "../features/routines/RoutinesListPage";
import { NewRoutineStub } from "../features/routines/NewRoutineStub";
import { PLACEHOLDERS } from "../components/ComingSoonPage";

/**
 * F-330 placeholder pages (T-007 + T-010).
 *
 * One shared lazy chunk powers all 11 placeholder routes — the single
 * `import()` call below is what every `PLACEHOLDERS.map(...)` entry
 * resolves through, so the bundler emits exactly one async chunk for
 * the whole table (technical spec § 4.3).
 */
const ComingSoonPage = lazy(
  () => import("../components/ComingSoonPage/ComingSoonPage"),
);

/**
 * F-341 DevicesPage — lazy chunk for `/devices`.
 *
 * Code-split so the page bundle stays out of the initial dashboard
 * payload; users only pay for it when they navigate to /devices. The
 * Suspense fallback is `null` (matches the F-330 placeholder pattern
 * above) — first paint is fast enough that a skeleton would flash.
 */
const DevicesPage = lazy(
  () => import("../features/devices/DevicesPage"),
);

/**
 * F-331 AccountPage — lazy chunk for `/account`.
 *
 * Same code-split rationale as `DevicesPage` above: keep the page out
 * of the initial dashboard bundle, share the `Suspense fallback={null}`
 * pattern with the F-330 placeholder routes so the chrome doesn't
 * flash a skeleton for a page that paints almost instantly.
 */
const AccountPage = lazy(
  () => import("../features/account/AccountPage"),
);

/**
 * F-332 PlanPage — lazy chunk for `/plan`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` above:
 * keep the page out of the initial dashboard bundle, share the
 * `Suspense fallback={null}` pattern with the F-330 placeholder routes
 * so the chrome doesn't flash a skeleton for a page that paints
 * almost instantly.
 */
const PlanPage = lazy(
  () => import("../features/plan/PlanPage"),
);

/**
 * F-336 HomesPage — lazy chunk for `/homes`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` above: keep the page out of the initial dashboard bundle,
 * share the `Suspense fallback={null}` pattern with the F-330
 * placeholder routes so the chrome doesn't flash a skeleton for a
 * page that paints almost instantly.
 */
const HomesPage = lazy(
  () => import("../features/homes/HomesPage"),
);

/**
 * F-333 RemoteAccessPage — lazy chunk for `/remote-access`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` above: keep the page out of the initial
 * dashboard bundle, share the `Suspense fallback={null}` pattern with
 * the F-330 placeholder routes so the chrome doesn't flash a skeleton
 * for a page that paints almost instantly.
 */
const RemoteAccessPage = lazy(
  () => import("../features/remote-access/RemoteAccessPage"),
);

/**
 * F-335 VoicePage — lazy chunk for `/voice`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` / `RemoteAccessPage` above: keep the page
 * out of the initial dashboard bundle, share the
 * `Suspense fallback={null}` pattern with the F-330 placeholder routes
 * so the chrome doesn't flash a skeleton for a page that paints almost
 * instantly.
 */
const VoicePage = lazy(
  () => import("../features/voice/VoicePage"),
);

/**
 * F-334 BackupsPage — lazy chunk for `/backups`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` / `RemoteAccessPage` / `VoicePage` above:
 * keep the page out of the initial dashboard bundle, share the
 * `Suspense fallback={null}` pattern with the F-330 placeholder routes
 * so the chrome doesn't flash a skeleton for a page that paints almost
 * instantly.
 */
const BackupsPage = lazy(
  () => import("../features/backups/BackupsPage"),
);

/**
 * F-337 ApiKeysPage — lazy chunk for `/api-keys`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` / `RemoteAccessPage` / `VoicePage` /
 * `BackupsPage` above: keep the page out of the initial dashboard
 * bundle, share the `Suspense fallback={null}` pattern with the F-330
 * placeholder routes so the chrome doesn't flash a skeleton for a page
 * that paints almost instantly.
 */
const ApiKeysPage = lazy(
  () => import("../features/api-keys/ApiKeysPage"),
);

/**
 * F-339 PublishPage — lazy chunk for `/publish`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` / `RemoteAccessPage` / `VoicePage` /
 * `BackupsPage` / `ApiKeysPage` above: keep the page out of the initial
 * dashboard bundle, share the `Suspense fallback={null}` pattern with
 * the F-330 placeholder routes so the chrome doesn't flash a skeleton
 * for a page that paints almost instantly.
 */
const PublishPage = lazy(
  () => import("../features/publish/PublishPage"),
);

/**
 * F-338 MarketplacePage — lazy chunk for `/marketplace`.
 *
 * Same code-split rationale as `DevicesPage` / `AccountPage` /
 * `PlanPage` / `HomesPage` / `RemoteAccessPage` / `VoicePage` /
 * `BackupsPage` / `ApiKeysPage` / `PublishPage` above: keep the page
 * out of the initial dashboard bundle, share the
 * `Suspense fallback={null}` pattern with the F-330 placeholder routes
 * so the chrome doesn't flash a skeleton for a page that paints almost
 * instantly.
 */
const MarketplacePage = lazy(
  () => import("../features/marketplace/MarketplacePage"),
);

/**
 * F-340 AnalyticsPage — lazy chunk for `/analytics`.
 *
 * Same code-split rationale as `MarketplacePage` and the other
 * destination pages above. With this in place, `PLACEHOLDERS` is
 * empty for the first time since F-330 shipped — all 11 sidebar
 * destinations are real pages.
 */
const AnalyticsPage = lazy(
  () => import("../features/analytics/AnalyticsPage"),
);

/**
 * F-613 ForgotPasswordPage — lazy chunk for `/account/forgot-password`.
 *
 * Public (guard-free) sibling of `/onboarding/account`, reached from the
 * cloud sign-in form's "Forgot password?" link. Stub for now — the full
 * reset email flow is a separate feature. Lazy-loaded with the same
 * `Suspense fallback={null}` pattern as the destination pages above.
 */
const ForgotPasswordPage = lazy(
  () => import("../features/account/ForgotPasswordPage"),
);

export const routes: RouteObject[] = [
  {
    path: ROUTES.ROOT,
    loader: rootIndexLoader,
    // Defensive fallback — loader normally redirects before this paints.
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    path: ROUTES.ONBOARDING,
    element: <OnboardingPage />,
  },
  {
    path: ROUTES.ONBOARDING_ACCOUNT,
    element: <SignInPage />,
  },
  {
    // F-613: forgot-password stub. Public, guard-free — a signed-out user
    // following "Forgot password?" must reach it without a session.
    path: ROUTES.FORGOT_PASSWORD,
    element: (
      <Suspense fallback={null}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    element: <OnboardingGuard />,
    children: [
      {
        element: <AuthGuard />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
              { path: ROUTES.ROUTINES, element: <RoutinesListPage /> },
              { path: ROUTES.NEW_ROUTINE, element: <NewRoutineStub /> },
              { path: ROUTES.SUGGESTIONS, element: <SuggestionsListPage /> },
              { path: ROUTES.SETTINGS, element: <SettingsPage /> },
              // F-341: dedicated DevicesPage. Listed BEFORE the
              // PLACEHOLDERS spread so the explicit route wins if
              // `/devices` ever sneaks back into the placeholder list
              // (it was removed in T-003, but defensive ordering is
              // cheap and self-documenting).
              {
                path: ROUTES.DEVICES,
                element: (
                  <Suspense fallback={null}>
                    <DevicesPage />
                  </Suspense>
                ),
              },
              // F-331: dedicated AccountPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/devices` above — `/account` was removed
              // from the placeholder table in T-002, but if it ever
              // sneaks back the explicit route wins.
              {
                path: ROUTES.ACCOUNT,
                element: (
                  <Suspense fallback={null}>
                    <AccountPage />
                  </Suspense>
                ),
              },
              // F-332: dedicated PlanPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/devices` and `/account` above — `/plan`
              // was removed from the placeholder table in F-332 T-002,
              // but if it ever sneaks back the explicit route wins.
              {
                path: ROUTES.PLAN,
                element: (
                  <Suspense fallback={null}>
                    <PlanPage />
                  </Suspense>
                ),
              },
              // F-336: dedicated HomesPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/plan` above — `/homes` was removed from
              // the placeholder table in F-336 T-002, but if it ever
              // sneaks back the explicit route wins.
              {
                path: ROUTES.HOMES,
                element: (
                  <Suspense fallback={null}>
                    <HomesPage />
                  </Suspense>
                ),
              },
              // F-333: dedicated RemoteAccessPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/homes` above — `/remote-access` was removed
              // from the placeholder table in F-333 T-002, but if it
              // ever sneaks back the explicit route wins.
              {
                path: ROUTES.REMOTE_ACCESS,
                element: (
                  <Suspense fallback={null}>
                    <RemoteAccessPage />
                  </Suspense>
                ),
              },
              // F-335: dedicated VoicePage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/remote-access` above — `/voice` was removed
              // from the placeholder table in F-335 T-002, but if it
              // ever sneaks back the explicit route wins.
              {
                path: ROUTES.VOICE,
                element: (
                  <Suspense fallback={null}>
                    <VoicePage />
                  </Suspense>
                ),
              },
              // F-334: dedicated BackupsPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/voice` above — `/backups` was removed from
              // the placeholder table in F-334 T-002, but if it ever
              // sneaks back the explicit route wins.
              {
                path: ROUTES.BACKUPS,
                element: (
                  <Suspense fallback={null}>
                    <BackupsPage />
                  </Suspense>
                ),
              },
              // F-337: dedicated ApiKeysPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/backups` above — `/api-keys` was removed
              // from the placeholder table in F-337 T-002, but if it
              // ever sneaks back the explicit route wins.
              {
                path: ROUTES.API_KEYS,
                element: (
                  <Suspense fallback={null}>
                    <ApiKeysPage />
                  </Suspense>
                ),
              },
              // F-339: dedicated PublishPage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/api-keys` above — `/publish` was removed
              // from the placeholder table in F-339 T-002, but if it
              // ever sneaks back the explicit route wins.
              {
                path: ROUTES.PUBLISH,
                element: (
                  <Suspense fallback={null}>
                    <PublishPage />
                  </Suspense>
                ),
              },
              // F-338: dedicated MarketplacePage. Listed BEFORE the
              // PLACEHOLDERS spread for the same defensive-ordering
              // reason as `/publish` above — `/marketplace` was removed
              // from the placeholder table in F-338 T-002, but if it
              // ever sneaks back the explicit route wins.
              {
                path: ROUTES.MARKETPLACE,
                element: (
                  <Suspense fallback={null}>
                    <MarketplacePage />
                  </Suspense>
                ),
              },
              // F-340: dedicated AnalyticsPage. Listed BEFORE the
              // PLACEHOLDERS spread — though PLACEHOLDERS is now empty,
              // the defensive ordering stays consistent with the sibling
              // destination pages above.
              {
                path: ROUTES.ANALYTICS,
                element: (
                  <Suspense fallback={null}>
                    <AnalyticsPage />
                  </Suspense>
                ),
              },
              // F-330: remaining placeholder routes — share one lazy chunk.
              // As of F-340 PLACEHOLDERS is empty; this spread is currently a
              // no-op but stays in place so future placeholders can be added
              // by appending a single entry to placeholderConfig.ts.
              ...PLACEHOLDERS.map((cfg) => ({
                path: cfg.route,
                element: (
                  <Suspense fallback={null}>
                    <ComingSoonPage config={cfg} />
                  </Suspense>
                ),
              })),
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.OFFLINE,
    element: <OfflinePage />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

/**
 * Default browser router. Imported by `App.tsx` once the orchestrator
 * wires the providers (see `INTEGRATION.md`).
 */
export const router = createBrowserRouter(routes);
